import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as JspUtil from '../common/JspUtil';
import submitHiddenForm from '../common/submitHiddenForm';
import { isYes } from '../common/Utils';
import { Dropdown, IceIcon, PageContainer, PageHeader, UserMenu } from '../components';
import * as actions from './actionCreators';
import { useJspVariables, useNavigation, useAppContext, useAuth, useDebugRender } from '../hooks';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Unstable_Grid2';

const PageAuthenticated = () => {
    useDebugRender('PageAuthenticated');
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { jspVariables, launcher, panelStyle } = useJspVariables();
    const { getLoginApp, getLabelFromJspVariables } = useAppContext(jspVariables);
    const { toPageChangePassword, toPageTfaRegister } = useNavigation();

    const { authenticatedUser, isLauncherAuth, isAuthenticated, loading } = useAuth();

    const [selectedUser, setSelectedUser] = useState(() => {
        return location?.state?.selectedUser || '';
    });
    const [selectedRelatedUser, setSelectedRelatedUser] = useState(() => {
        return location?.state?.selectedRelatedUser || '';
    });
    const [redirectUrl] = useState(() => {
        if (jspVariables.jspRedirectUrl) return jspVariables.jspRedirectUrl;
        if (JspUtil.isValueAvailable(window.jspRedirectUrl)) return window.jspRedirectUrl;
        return '';
    });
    const [checkSession] = useState(() => {
        if (location?.state?.checkSession) return location.state.checkSession;
        if (JspUtil.isValueAvailable(window.jspCheckSession)) return window.jspCheckSession;
        return '';
    });
    const [isMemberLogin] = useState(() => JspUtil.isValueAvailable(window.jspMemberLogin));
    const [showAppSelector, setShowAppSelector] = useState(false);
    const [showRelatedAppSelector, setShowRelatedAppSelector] = useState(false);
    const [userOptions, setUserOptions] = useState([]);
    const [relatedUserOptions, setRelatedUserOptions] = useState([]);

    const isSaml = JspUtil.isValueAvailable(window.jspSamlSession);
    const isSamlLogin = JspUtil.isValueAvailable(window.jspSamlLogin);
    const appKeyVal = isLauncherAuth ? (authenticatedUser?.app || '') : getLoginApp();

    const getLoginUrl = () => {
        if (isMemberLogin) return '/memberLogin';
        if (isSamlLogin && !isSaml) return '/samlUserLogin';
        return '/appUserLogin';
    };

    const buildFormFields = () => ({
        redirectUrl,
        loginUserId: selectedUser,
        selectedRelatedUser,
        loginApp: appKeyVal,
        checkSession,
        isLauncher: isLauncherAuth ? 'Y' : 'N',
        samlSession: isSaml ? window.jspSamlSession : undefined,
        samlLogin: isSamlLogin ? window.jspSamlLogin : undefined,
        secondAuth: JspUtil.isValueAvailable(window.jspSecondAuth) ? window.jspSecondAuth : undefined,
    });

    // Guards against re-submitting the login form when an authenticated user
    // re-enters /pageAuthenticated (e.g. the session-check override "Continue",
    // where the server keeps re-emitting jspCheckSession). Without this, each
    // re-entry fires submitLoginForm() again → infinite reload loop.
    const hasSubmittedRef = useRef(false);

    const submitLoginForm = () => {
        if (hasSubmittedRef.current) return;
        hasSubmittedRef.current = true;
        submitHiddenForm(getLoginUrl(), buildFormFields());
    };

    const getAppCount = (appNames, userNames) => {
        if (authenticatedUser || JspUtil.isValueAvailable(window.jspSamlLogin)) {
            if (JspUtil.isValueAvailable(window.jspLinkCandidates)) {
                const linkstr = window.jspLinkCandidates;
                const loginAppKey = getLoginApp();
                if (linkstr && loginAppKey) {
                    let count = 0;
                    const links = JSON.parse(linkstr);
                    if (links.length > 0) {
                        links.forEach(function (linkInfo) {
                            appNames.push(linkInfo.app);
                            let name = linkInfo.display;
                            if (linkInfo.showComp === "Y" && linkInfo.cname) {
                                name = name + " - " + linkInfo.cname;
                            }
                            userNames.push({ l: name, v: linkInfo.uname });
                            count++;
                        });
                    }
                    return count;
                }
            }
        }
        return 0;
    };

    const getRelatedAppCount = (appNames, userNames) => {
        if (authenticatedUser || JspUtil.isValueAvailable(window.jspSamlLogin)) {
            if (JspUtil.isValueAvailable(window.jspLinkRelatedCandidates) && JspUtil.isValueAvailable(window.jspRelatedApp)) {
                const linkstr = window.jspLinkRelatedCandidates;
                const relatedAppKey = window.jspRelatedApp;
                if (linkstr && relatedAppKey) {
                    let count = 0;
                    const links = JSON.parse(linkstr);
                    if (links.length > 0) {
                        links.forEach(function (linkInfo) {
                            appNames.push(linkInfo.app);
                            let name = linkInfo.display;
                            if (linkInfo.showComp === "Y" && linkInfo.cname) {
                                name = name + " - " + linkInfo.cname;
                            }
                            userNames.push({ l: name, v: linkInfo.uname });
                            count++;
                        });
                    }
                    return count;
                }
            }
        }
        return 0;
    };

    const handleAppSelectors = () => {
        const appNames = [];
        const userNamesArr = [];
        const relatedAppNames = [];
        const relatedUserNamesArr = [];

        if (getAppCount(appNames, userNamesArr) > 1) {
            setUserOptions(userNamesArr);
            setShowAppSelector(true);
            return true;
        } else {
            setShowAppSelector(false);
        }
        if (getRelatedAppCount(relatedAppNames, relatedUserNamesArr) > 1) {
            setRelatedUserOptions(relatedUserNamesArr);
            setShowRelatedAppSelector(true);
            return true;
        } else {
            setShowRelatedAppSelector(false);
        }
        return false;
    };

    useEffect(() => {
        // Compute selector visibility synchronously up front. setShowAppSelector
        // does not update `showAppSelector` within this same effect run, so the
        // gates below must key off these local values — not the state — otherwise
        // a session-check submit could fire while a selector is actually needed
        // and bypass it.
        const appNames = [];
        const userNamesArr = [];
        const showAppSelectorNow = isAuthenticated && getAppCount(appNames, userNamesArr) > 1;
        const relatedAppNames = [];
        const relatedUserNamesArr = [];
        const showRelatedAppSelectorNow = isAuthenticated && getRelatedAppCount(relatedAppNames, relatedUserNamesArr) > 1;

        if (isAuthenticated) {
            if (isMemberLogin) {
                navigate(constants.ROUTE_PAGE_MEMBER_LOGIN);
                return;
            }
            if (showAppSelectorNow) {
                setShowAppSelector(true);
            }
            setUserOptions(userNamesArr);
            if (showRelatedAppSelectorNow) {
                setShowRelatedAppSelector(true);
            }
            setRelatedUserOptions(relatedUserNamesArr);
        }

        // Only submit for an actual session-check request. A checkSession of 'N'
        // is the user's "continue with multiple sessions" override — the server
        // has already resolved it, so re-submitting would loop. Treat 'N' (and
        // empty) as "no check needed" and fall through to the normal auth flow.
        const needsSessionCheck = isYes(checkSession);
        if (needsSessionCheck && !showAppSelectorNow) {
            submitLoginForm();
            return;
        }

        if (isAuthenticated) {
            // A selector is needed → stop here and let the user pick; the options
            // were already set above. (setUserOptions/setRelatedUserOptions done.)
            if (showAppSelectorNow || showRelatedAppSelectorNow) return;
            if (JspUtil.isValueAvailable(window.jspSamlSession)) {
                submitLoginForm();
                return;
            }
            if (JspUtil.isValueAvailable(window.jspSamlLogin)) {
                submitLoginForm();
                return;
            }
            if (jspVariables.jspPage === 'UPDATE_REGISTRATION') {
                navigate(constants.ROUTE_PAGE_UPDATE_REGISTRATION);
                return;
            }

            if (redirectUrl || jspVariables.jspRedirectUrl) {
                submitLoginForm();
            } else {
                navigate(constants.ROUTE_PAGE_USERINFO);
            }
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && JspUtil.isValueAvailable(window.jspSamlLogin)) {
            handleAppSelectors();
        }
    }, [isAuthenticated]);

    if (loading) return null;
    if (!authenticatedUser) return <Navigate to={constants.ROUTE_PAGE_LOGIN} replace />;

    const logoutUserToken = () => {
        dispatch(actions.logoutUserToken(authenticatedUser.user, true, false));
        const action = isSamlLogin ? '/appUserLogout' : getLoginUrl();
        submitHiddenForm(action, buildFormFields());
    };

    const onCandidateUserNameChange = (val) => {
        if (showRelatedAppSelector) {
            setSelectedRelatedUser(val);
        } else {
            setSelectedUser(val);
        }
    };

    const onContinueClick = () => {
        submitLoginForm();
    };

    let showInfo = true;
    if (redirectUrl || showAppSelector || showRelatedAppSelector) {
        showInfo = false;
    }

    return (
        <PageContainer id="pageAuthenticated" dataAutomationId="page_authenticated">
            {(showInfo || showAppSelector || showRelatedAppSelector) &&
                <Box style={panelStyle}>
                    <UserMenu
                        authenticatedUser={authenticatedUser}
                        onLogout={logoutUserToken}
                        onNavigate={(route) => navigate(route)}
                    />
                    {showAppSelector &&
                        <Grid container>
                            <Grid xs={12}>
                                <Typography fontWeight={500}>{getLabelFromJspVariables('title_sso_choose_link')}</Typography>
                                <Dropdown selectedValue={selectedUser}
                                    labelProperty="l"
                                    valueProperty="v"
                                    options={userOptions}
                                    id="authenticated_app_user_select"
                                    name="selectedUser"
                                    dataAutomationId="authenticated_app_user_select"
                                    updateValue={val => onCandidateUserNameChange(val)} />
                            </Grid>
                            <Grid xs={12} display="flex" justifyContent="flex-end">
                                <Button variant="contained" fullWidth
                                    onClick={onContinueClick}
                                    data-automation-id="authenticated_app_user_continue_btn">
                                    {getLabelFromJspVariables('action_continue')}
                                </Button>
                            </Grid>
                        </Grid>
                    }
                    {showRelatedAppSelector &&
                        <Grid container>
                            <Grid xs={12}>
                                <Typography fontWeight={500}>{getLabelFromJspVariables('title_sso_choose_link')}</Typography>
                                <Dropdown selectedValue={selectedRelatedUser}
                                    labelProperty="l"
                                    valueProperty="v"
                                    options={relatedUserOptions}
                                    id="authenticated_relatedapp_user_select"
                                    name="selectedRelatedUser"
                                    dataAutomationId="authenticated_relatedapp_user_select"
                                    updateValue={val => onCandidateUserNameChange(val)} />
                            </Grid>
                            <Grid xs={12} display="flex" justifyContent="flex-end">
                                <Button variant="contained" fullWidth
                                    onClick={onContinueClick}
                                    data-automation-id="authenticated_relatedapp_user_continue_btn">
                                    {getLabelFromJspVariables('action_continue')}
                                </Button>
                            </Grid>
                        </Grid>
                    }
                    {showInfo &&
                        <Box>
                            <IceIcon launcher={launcher} />
                            <PageHeader dataAutomationId="authenticated_header">{getLabelFromJspVariables('sso_menu')}</PageHeader>
                            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                                <li>
                                    <Link component="button" variant="body2" onClick={toPageChangePassword}
                                        data-automation-id="authenticated_change_password_link">{getLabelFromJspVariables('change_password')}</Link>
                                </li>
                                <li>
                                    <Link component="button" variant="body2" onClick={toPageTfaRegister}
                                        data-automation-id="authenticated_tfa_register_link">{getLabelFromJspVariables('update_tfa_lost')}</Link>
                                </li>
                            </Box>
                            <Grid container>
                                <Grid xs={12}>
                                    <Typography fontWeight={500} component="span"> Links: </Typography>
                                    {authenticatedUser.links}
                                </Grid>
                                <Grid xs={12}>
                                    <Typography fontWeight={500} component="span"> Password Expires: </Typography>
                                    {authenticatedUser.passwordExpiration}
                                </Grid>
                                <Grid xs={12}>
                                    <Typography fontWeight={500} component="span"> Roles: </Typography>
                                    {authenticatedUser.roles ? authenticatedUser.roles.join(", ") : ""}
                                </Grid>
                            </Grid>
                        </Box>
                    }
                </Box>
            }
        </PageContainer>
    );
};

export default PageAuthenticated;
